import React from 'react';
import { ChartSchema } from '../../../types/types';

interface BarChartRendererProps {
  series: ChartSchema['series'];
  xScale: (value: string | number) => number;
  yScale: (value: number) => number;
  yMinMax?: { min: number; max: number };
}

/**
 * Standard Vertical Bar Chart Renderer Component
 */
export const BarChartRenderer: React.FC<BarChartRendererProps> = ({ series, xScale, yScale, yMinMax }) => {
  // Get unique x values for bar positioning
  const uniqueXValues = Array.from(new Set(series.flatMap(s => s.data.map(d => d.x))));
  const seriesCount = series.length;

  // Calculate the width for each bar group
  const maxBarWidth = 40; // Maximum width of a single bar
  const barWidth = Math.min(maxBarWidth, 80 / seriesCount); // Ensure bars aren't too wide

  // Get the baseline for the y-axis (always 0 for bar charts)
  // Make sure the baseline is visible within the chart area
  const minValue = yMinMax?.min || 0;
  const maxValue = yMinMax?.max || 100;

  // Use 0 as baseline, but ensure it's within the visible range
  const baselineValue = Math.max(minValue, Math.min(maxValue, 0));
  const baselineY = yScale(baselineValue);

  return (
    <g className="bar-chart">
      {series.map((series, seriesIndex) => (
        <g key={`bar-series-${series.id}`} className="bar-series">
          {series.data.map((point, i) => {
            // Calculate position
            const xCenter = xScale(point.x);
            const yValue = yScale(point.y);

            // Calculate x position with offset for grouped bars
            // This ensures bars are centered around their x position
            const xOffset = (seriesIndex - (seriesCount - 1) / 2) * barWidth;
            const x = xCenter + xOffset;

            // For bar charts, we always draw from the baseline (0) up or down
            // Calculate the correct height and position based on value
            let barY, barHeight;

            if (point.y >= baselineValue) {
              // Positive values - draw up from baseline
              barY = yValue;
              barHeight = baselineY - yValue;
            } else {
              // Negative values - draw down from baseline
              barY = baselineY;
              barHeight = yValue - baselineY;
            }

            // Ensure minimum height for visibility
            barHeight = Math.max(barHeight, 1);

            // If the value is outside the visible range, don't render the bar
            if (point.y < minValue || point.y > maxValue) {
              barHeight = 0;
            }

            return (
              <g key={`bar-${series.id}-${i}`} className="bar">
                <rect
                  x={x - barWidth / 2 + barWidth * 0.05}
                  y={barY}
                  width={barWidth * 0.9} // 90% of calculated width to add spacing
                  height={barHeight}
                  fill={series.color}
                  opacity={0.9}
                  rx={2} // Rounded corners
                />

                {/* Value label */}
                {point.y !== 0 && (
                  <text x={x} y={point.y >= 0 ? yValue - 8 : yValue + 16} textAnchor="middle" fontSize="10" fill="#666">
                    {point.y}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      ))}
    </g>
  );
};

export default BarChartRenderer;
